import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from '../types/user';
import logo from '../assets/minister.png';
import { BellIcon, LogoutIcon, MenuIcon } from './icons';
import { socket } from '../lib/socket';
import {
  getNotificationList,
  markAllAsRead as markAllAsReadApi,
  markOneAsRead,
  type NotificationItem,
} from '../services/settingServices';

type HeaderProps = {
  user: User;
  onLogout: () => void;
  onMenuOpen: () => void;
};

// Map form keys to their specific detail routes
const FORM_DETAIL_ROUTE_MAP: Record<string, string> = {
  PLO_FORM_ONE: '/plo-form-one-detail',
  PLO_FORM_TWO: '/plo-form-two-detail',
  PLO_FORM_THREE: '/plo-form-three-detail',
  PLO_FORM_FOUR: '/plo-form-four-detail',
  PLO_FORM_FIVE: '/plo-form-five-detail',
  PLO_FORM_SIX: '/plo-form-six-detail',
  LEGAL_ADVICE_FORM_ONE: '/legal-form-one-detail',
  LEGAL_ADVICE_FORM_TWO: '/legal-form-two-detail',
  LEGAL_ADVICE_FORM_THREE: '/legal-form-three-detail',
  LEGAL_ADVICE_FORM_FOUR: '/legal-form-four-detail',
  LEGAL_ADVICE_FORM_FIVE: '/legal-form-five-detail',
  LEGAL_ADVICE_FORM_SIX: '/legal-form-six-detail',
  LEGAL_ADVICE_FORM_SEVEN: '/legal-form-seven-detail',
  LEGAL_ADVICE_FORM_EIGHT: '/legal-form-eight-detail',
   LEGAL_ADVICE_FORM_SEVEN_A: '/legal-form-seven-a-detail',
  LEGAL_ADVICE_FORM_EIGHT_A: '/legal-form-eight-a-detail',
  LEGAL_ADVICE_FORM_NINE: '/legal-form-nine-detail',
  LEGAL_ADVICE_FORM_TEN: '/legal-form-ten-detail',
  LEGAL_ADVICE_FORM_ELEVEN: '/legal-form-eleven-detail',
};

const FORM_EDIT_ROUTE_MAP: Record<string, string> = {
  PLO_FORM_ONE: '/plo-form-one/edit',
  PLO_FORM_TWO: '/plo-form-two/edit',
  PLO_FORM_THREE: '/plo-form-three/edit',
  PLO_FORM_FOUR: '/plo-form-four/edit',
  PLO_FORM_FIVE: '/plo-form-five/edit',
  PLO_FORM_SIX: '/plo-form-Six/edit',
  LEGAL_ADVICE_FORM_ONE: '/legal-form-one/edit',
  LEGAL_ADVICE_FORM_TWO: '/legal-form-two/edit',
  LEGAL_ADVICE_FORM_THREE: '/legal-form-three/edit',
  LEGAL_ADVICE_FORM_FOUR: '/legal-form-four/edit',
  LEGAL_ADVICE_FORM_FIVE: '/legal-form-five/edit',
  LEGAL_ADVICE_FORM_SIX: '/legal-form-six/edit',
  LEGAL_ADVICE_FORM_SEVEN: '/legal-form-seven/edit',
  LEGAL_ADVICE_FORM_EIGHT: '/legal-form-eight/edit',
  LEGAL_ADVICE_FORM_SEVEN_A: '/legal-form-seven-a/edit',
  LEGAL_ADVICE_FORM_EIGHT_A: '/legal-form-eight-a/edit',
  LEGAL_ADVICE_FORM_NINE: '/legal-form-nine/edit',
  LEGAL_ADVICE_FORM_TEN: '/legal-form-ten/edit',
  LEGAL_ADVICE_FORM_ELEVEN: '/legal-form-eleven/edit',
};

const normalizeFormType = (value?: string | null): string => {
  if (!value) return '';

  const cleaned = value.trim().replace(/[-\s]+/g, '_');
  return cleaned.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase();
};

const normalizeNotificationItem = <T extends { formType?: string | null }>(item: T): T & { formType: string } => ({
  ...item,
  formType: normalizeFormType(item.formType),
});

// Fix Vite ENV variable resolution
export function Header({ user, onLogout, onMenuOpen }: HeaderProps) {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user?.id) return;

    getNotificationList()
      .then((res: any) => {
        const payload = res?.data?.items ? res.data : res;

        if (payload && Array.isArray(payload.items)) {
          const normalizedItems = payload.items.map((item: NotificationItem) =>
            normalizeNotificationItem(item)
          );

          setNotifications((previous) => {
            const fetchedIds = new Set(normalizedItems.map((item: NotificationItem) => item.id));
            const liveItems = previous.filter((item) => !fetchedIds.has(item.id));
            return [...liveItems, ...normalizedItems];
          });
          setUnreadCount((previous) => Math.max(previous, payload.unreadCount ?? 0));
        }
      })
      .catch((err: any) =>
        console.error('Failed to load initial notifications:', err)
      );

    const handleConnect = () => {
      console.info('[socket] connected:', socket.id);
      socket.emit('join', user.id);
      console.info('[socket] join sent for user:', user.id);
    };
    const handleNewNotification = (newNoti: NotificationItem) => {
      const normalizedNotification = normalizeNotificationItem(newNoti);
      console.info('[socket] new_notification received:', normalizedNotification);
      setNotifications((previous) => {
        if (previous.some((item) => item.id === normalizedNotification.id)) return previous;
        return [normalizedNotification, ...previous];
      });
      setUnreadCount((previous) => previous + 1);
    };

    socket.on('connect', handleConnect);
    socket.on('new_notification', handleNewNotification);

    if (socket.connected) {
      handleConnect();
    } else {
      socket.connect();
    }

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };
  document.addEventListener('mousedown', handleClickOutside);

  return () => {
      socket.off('connect', handleConnect);
      socket.off('new_notification', handleNewNotification);
      socket.disconnect();
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [user?.id]);

  // Handle Mark All as Read click
  const handleMarkAllRead = async () => {
    try {
      setUnreadCount(0); // Optimistic UI update
      setNotifications((prev) =>
        prev.map((item) => ({ ...item, isRead: true }))
      );
      await markAllAsReadApi();
    } catch (err) {
      console.error('Failed to mark notifications as read:', err);
    }
  };

  const handleNotificationClick = (notification: NotificationItem) => {
    markOneAsRead(notification.documentId!);
    setIsOpen(false);

    const normalizedFormType = normalizeFormType(notification.formType);
    if (!normalizedFormType || !notification.documentId) return;

    const shouldOpenEditForm = notification.message.includes('ကြည့်ရှုခဲ့သည်');
    const routePrefix = shouldOpenEditForm
      ? FORM_EDIT_ROUTE_MAP[normalizedFormType]
      : FORM_DETAIL_ROUTE_MAP[normalizedFormType];
    if (routePrefix) {
      navigate(`${routePrefix}/${notification.documentId}`);
    } else {
      console.warn(
        `No detail route mapping found for formType: ${notification.formType}`
      );
    }
  };

  return (
    <header className="sticky top-0 z-30 border-b border-slate-200 bg-white shadow-sm">
      <div className="flex h-16 items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={onMenuOpen}
            className="rounded-lg p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-blue-700 lg:hidden"
            aria-label="Open navigation menu"
          >
            <MenuIcon className="h-5 w-5" />
          </button>

          <div className="flex min-w-0 items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center sm:h-10 sm:w-10">
              <img
                src={logo}
                alt="ဥပဒေရေးရာဝန်ကြီးဌာန"
                className="h-full w-full object-contain"
              />
            </div>

            <div className="min-w-0">
              <h1 className="truncate text-sm font-semibold text-slate-900 sm:text-base">
                ဥပဒေရေးရာဝန်ကြီးဌာန
              </h1>
              <p className="hidden text-xs text-slate-500 sm:block">
                ဥပဒေဆိုင်ရာ အချက်အလက် စီမံခန့်ခွဲမှုစနစ်
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          <div className="hidden text-right md:block">
            <p className="text-sm font-medium text-slate-900">{user.name}</p>
            <p className="text-xs text-slate-500">{user.role}</p>
          </div>

          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setIsOpen((prev) => !prev)}
              className="relative rounded-lg p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-blue-700"
              aria-label={`Notifications, ${unreadCount} unread`}
            >
              <BellIcon className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-600 text-[10px] font-semibold text-white">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {isOpen && (
              <div className="absolute right-0 mt-2 w-80 rounded-xl border border-slate-200 bg-white shadow-lg z-50 overflow-hidden">
                <div className="flex items-center justify-between bg-slate-50 px-4 py-3 border-b border-slate-200">
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    အကြောင်းကြားချက်များ
                  </h4>
                  {unreadCount > 0 && (
                    <button
                      onClick={handleMarkAllRead}
                      className="text-[11px] font-medium text-blue-600 hover:underline"
                    >
                      ဖတ်ပြီးသားအဖြစ်မှတ်သားရန်
                    </button>
                  )}
                </div>

                <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
                  {notifications.length === 0 ? (
                    <p className="p-4 text-center text-xs text-slate-400">
                      အကြောင်းကြားချက် မရှိသေးပါ
                    </p>
                  ) : (
                    notifications.map((n, idx) => (
                      <div
                        key={n.id || idx}
                        onClick={() => handleNotificationClick(n)}
                        className={`p-3 text-left transition-colors cursor-pointer ${
                          !n.isRead ? 'bg-blue-50/50 hover:bg-blue-50' : 'hover:bg-slate-50'
                        }`}
                      >
                        <p className="text-xs font-semibold text-slate-800">
                          {n.title}
                        </p>
                        <p className="mt-0.5 text-[11px] text-slate-600">
                          {n.message}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={onLogout}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
          >
            <LogoutIcon className="h-4 w-4" />
            <span className="hidden sm:inline">ထွက်မည်</span>
          </button>
        </div>
      </div>
    </header>
  );
}